import React, { useState, useEffect } from 'react';
import { supabase } from '../api/supabase';
import { Box, Typography, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Alert, Grid, Card, CardContent, CardActions, CardMedia } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CakeIcon from '@mui/icons-material/Cake';
import Loader from '../components/common/Loader';

// Card de Produto com design aprimorado e exibição de estoque
const ProductItemCard = ({ product, onEdit }) => {
  const imageUrl = product.image_url ? `${process.env.PUBLIC_URL}/images/${product.image_url}` : '';
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardMedia component="div" sx={{ height: 160, backgroundColor: !imageUrl ? '#FCE4EC' : 'transparent', backgroundImage: imageUrl ? `url(${imageUrl})` : 'none', backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {!imageUrl && <CakeIcon sx={{ fontSize: 60, color: 'primary.light' }} />}
      </CardMedia>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom>{product.name}</Typography>
        <Typography color="text.secondary">Venda: {Number(product.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Typography>
        <Typography color="text.secondary">Custo: {Number(product.cost_price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Typography>
        <Typography color={product.quantity > 5 ? 'text.primary' : 'error.main'} sx={{ fontWeight: 'bold', mt: 1 }}>
          Estoque: {product.quantity}
        </Typography>
      </CardContent>
      <CardActions>
        <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(product)}>Editar</Button>
      </CardActions>
    </Card>
  );
};

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProduct, setCurrentProduct] = useState({ id: null, name: '', price: '', cost_price: '', image_url: '', quantity: 0 });
  const [error, setError] = useState('');

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('products').select('*').order('name');
    if (error) setError('Falha ao buscar produtos.');
    else setProducts(data || []);
    setLoading(false);
  };

  const handleOpen = (product = null) => {
    if (product) {
      setIsEditing(true);
      setCurrentProduct(product);
    } else {
      setIsEditing(false);
      setCurrentProduct({ id: null, name: '', price: '', cost_price: '', image_url: '', quantity: 0 });
    }
    setError('');
    setOpen(true);
  };

  const handleClose = () => { setOpen(false); };

  const handleSave = async () => {
    if (!currentProduct.name || !currentProduct.price || !currentProduct.cost_price) {
      setError('Nome, Preço de Venda e Preço de Custo são obrigatórios.');
      return;
    }
    const productData = {
      name: currentProduct.name,
      price: parseFloat(currentProduct.price),
      cost_price: parseFloat(currentProduct.cost_price),
      quantity: parseInt(currentProduct.quantity, 10) || 0,
      image_url: currentProduct.image_url || null,
    };

    const { error: saveError } = isEditing
      ? await supabase.from('products').update(productData).eq('id', currentProduct.id)
      : await supabase.from('products').insert([productData]);

    if (saveError) { setError('Falha ao salvar o produto.'); } 
    else { handleClose(); fetchProducts(); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentProduct(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Gerenciar Produtos</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>Novo Produto</Button>
      </Box>
      
      {loading ? <Loader /> : (
        <Grid container spacing={2}>
          {products.map(product => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <ProductItemCard product={product} onEdit={handleOpen} />
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>{isEditing ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField autoFocus margin="dense" name="name" label="Nome do Produto" fullWidth value={currentProduct.name} onChange={handleChange} />
          <TextField margin="dense" name="price" label="Preço de Venda (R$)" type="number" fullWidth value={currentProduct.price} onChange={handleChange} />
          <TextField margin="dense" name="cost_price" label="Preço de Custo (R$)" type="number" fullWidth value={currentProduct.cost_price} onChange={handleChange} />
          <TextField margin="dense" name="quantity" label="Quantidade em Estoque" type="number" fullWidth value={currentProduct.quantity} onChange={handleChange} />
          <TextField margin="dense" name="image_url" label="Nome do Arquivo da Imagem" placeholder="Ex: trufa.jpg" helperText="A imagem deve estar na pasta /public/images/" fullWidth value={currentProduct.image_url} onChange={handleChange} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">{isEditing ? 'Salvar Alterações' : 'Adicionar Produto'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsPage;